import { create } from './apiClient';
import { aws } from '../config';
import { storeRedirect } from '../helpers/postLoginRedirect';

let bzAuthToken = null;
try {
  bzAuthToken = localStorage.getItem('bzAuthToken');
} catch (e) {
  console.log('No token found for BZAPI in localstorage');
}

const apiClient = create({
  baseURL: aws.apiGateway.URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: bzAuthToken ? `Bearer ${bzAuthToken}` : ''
  },
  timeout: 20000
});

const authMonitor = response => {
  if (response.status === 401) {
    console.log('Receieved a unauthorized. Remove token and redirecting..');
    localStorage.removeItem('bzAuthToken');
    storeRedirect(window.location);
    window.location.href = aws.cognito.loginUrl;
  }
  if (response.status === 403) {
    console.log('Receieved a FORBIDDEN, redirecting..');
    window.location.href = '/authentication/forbidden';
  }
};
// Montior API for 401s
apiClient.addMonitor(authMonitor);

// BSA
async function bsaCreateProject(data) {
  const response = await apiClient.post(`/bsa/project`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaCreateVersionComment(data) {
  const response = await apiClient.post(`/bsa/project/version/comment`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaDeleteVersionComment(data) {
  const response = await apiClient.delete(`/bsa/project/version/comment`, {}, { data });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaDeleteProject(projectId) {
  const response = await apiClient.delete(`/bsa/project/${projectId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaCreateBugOrCveComment(data) {
  const response = await apiClient.post(`/bsa/project/version/bug-cve/comment`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaDeleteBugOrCveComment(data) {
  const response = await apiClient.delete(`/bsa/project/version/bug-cve/comment`, {}, { data: data });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaSetVersionState(data) {
  const response = await apiClient.put(`/bsa/project/version/state`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaSetVersionBugCveState(data) {
  const response = await apiClient.put(`/bsa/project/version/bug-cve/state`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function bsaListProjects() {
  const response = await apiClient.get(`/bsa/listProjects`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaGetProject(bsaProjectId) {
  const response = await apiClient.get(`/bsa/project/${bsaProjectId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaListProjectVersions(bsaProjectVersionId) {
  const response = await apiClient.get(`/bsa/project/version/${bsaProjectVersionId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
/**
 * Vendors that can run a live inventory scan (the backend's adapter registry).
 * Returns the vendor-id list; callers treat a failure as "no vendor can scan"
 * so nobody is offered a scan that cannot run (STRY0038603).
 */
async function bsaInvVendors() {
  const response = await apiClient.get('/bsa/inventory/vendors');
  if (response.status === 200 || response.status === 201) {
    const vendors = response.data && response.data.vendors;
    return Array.isArray(vendors) ? vendors : [];
  }
  throw new Error('Request Failed');
}
/**
 * On-demand device counts for one or more managed products (STRY0038414).
 * Returns { counts: [{ managedProductId, ciCount }], unsupported: [id], unavailable: [id] }.
 * `unsupported` is definitive (no adapter, or no count mode) -- safe to cache
 * as a permanent "no count". `unavailable` is transient (missing/disabled
 * product, ServiceNow not configured, or a CMDB read failure) -- must be
 * re-requested on a later visit (finding 2+3). Nothing is cached
 * server-side; the session cache lives in the portal.
 */
async function bsaInvCounts(managedProductIds) {
  const response = await apiClient.post('/bsa/inventory/counts', { managedProductIds });
  if (response.status === 200 || response.status === 201) {
    const data = response.data || {};
    return {
      counts: Array.isArray(data.counts) ? data.counts : [],
      unsupported: Array.isArray(data.unsupported) ? data.unsupported : [],
      unavailable: Array.isArray(data.unavailable) ? data.unavailable : []
    };
  }
  throw new Error('Request Failed');
}
// Accepts either a bare managedProductId (legacy call sites, kept working
// unchanged) or a params object — { managedProductId } or the discovery
// flow's { vendorId, productName } — posted through as-is.
async function bsaInvStartFetch(managedProductIdOrParams) {
  const body =
    managedProductIdOrParams !== null && typeof managedProductIdOrParams === 'object'
      ? managedProductIdOrParams
      : { managedProductId: managedProductIdOrParams };
  const response = await apiClient.post('/bsa/inventory/fetch', body);
  if (response.status === 200 || response.status === 201 || response.status === 202)
    return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaInvGetFetchJob(jobId) {
  const response = await apiClient.get(`/bsa/inventory/fetch/${jobId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
// body is forwarded unchanged — the managed-product flow sends
// { managedProductId, ... }, the discovery flow sends { vendorId, productId, ... }
// with no managedProductId. This function never inspects shape.
async function bsaInvCreateProjectFromInventory(body) {
  const response = await apiClient.post('/bsa/inventory/project', body);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
// Discovery scan (sync fast path): what ServiceNow's CMDB has installed for
// this vendor, for tenants with no managed product configured yet. On success
// returns the discovery data. On a slow/timeout or server error, throws an
// error tagged scanAvailable=true so the caller can offer the async scan; on a
// 4xx (e.g. ServiceNow not configured) throws a plain error — a scan would fail
// the same way, so only the catalog is offered.
const DISCOVER_SCAN_PROBLEMS = ['TIMEOUT_ERROR', 'NETWORK_ERROR', 'CONNECTION_ERROR', 'SERVER_ERROR'];
async function bsaInvDiscover(vendorId) {
  const response = await apiClient.get(`/bsa/inventory/discover/${encodeURIComponent(vendorId)}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  const err = new Error('Request Failed');
  if (DISCOVER_SCAN_PROBLEMS.includes(response.problem) || (response.status && response.status >= 500)) {
    err.scanAvailable = true;
  }
  throw err;
}
// Discovery scan (async): starts the CMDB scan as a job for large fleets that
// exceed the sync timeout. Returns { jobId }; poll with bsaInvGetFetchJob.
async function bsaInvStartDiscoverScan(vendorId) {
  const response = await apiClient.post('/bsa/inventory/discover', { vendorId });
  if (response.status === 200 || response.status === 201 || response.status === 202)
    return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaInvRefreshSnapshot(bsaProjectId, fetchJobId) {
  const response = await apiClient.post(`/bsa/project/${bsaProjectId}/inventory/refresh`, { fetchJobId });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaInvGetConfigRemoved(bsaProjectId, versionId) {
  const response = await apiClient.get(`/bsa/project/${bsaProjectId}/config-removed`, { versionId });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaInvSetComponentOverride(bsaProjectId, component, enabled) {
  const response = await apiClient.put(`/bsa/project/${bsaProjectId}/inventory/component`, {
    component,
    enabled
  });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaInvGetManualComponents(bsaProjectId) {
  const response = await apiClient.get(`/bsa/project/${bsaProjectId}/inventory/manual-components`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

// BSA -> ODD proxy (Cognito-authorized). Forwards query params to ODD's
// GET /bst/<endpoint> and returns ODD's response body unchanged.
async function bsaGetBstVendors() {
  const response = await apiClient.get('/bsa/odd/getBstVendors');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaGetBstVendorProducts(vendorId) {
  const response = await apiClient.get('/bsa/odd/getBstVendorProducts', { vendor_id: vendorId });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaGetBstSoftwareSuggestions(vendorId, productId, currentVersion) {
  const params = { vendor_id: vendorId, product_id: productId };
  if (currentVersion) params.current_version = currentVersion;
  const response = await apiClient.get('/bsa/odd/getBstSoftwareSuggestions', params);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bsaGetBstCves(vendorId, softwareType, version) {
  const response = await apiClient.get('/bsa/odd/getBstCves', {
    vendor_id: vendorId,
    software_type: softwareType,
    version
  });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
/**
 * `productName` accepts a single name or an array of them. An array becomes
 * `product_names`, which the backend resolves in one query — a device-scoped
 * project spanning 29 modules costs the same round trip as a single-module
 * one, so there is no reason for callers to fan out.
 */
async function bsaGetBstBugs(vendorId, productName, version) {
  // product_name is sent alongside product_names on purpose: a backend that
  // predates the multi-module query ignores product_names and would otherwise
  // fail its required-param check and return nothing at all. With both, an old
  // backend degrades to the single-module result it always gave, and a new one
  // prefers the list.
  const names = Array.isArray(productName) ? productName : null;
  const response = await apiClient.get('/bsa/odd/getBstBugs', {
    vendor_id: vendorId,
    product_name: names ? names[0] : productName,
    ...(names ? { product_names: names.join(',') } : {}),
    version
  });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function bsaStartExport(data) {
  const response = await apiClient.post(`/bsa/export/start`, data);
  if (response.status === 200 || response.status === 201 || response.status === 202)
    return response.data ? response.data : null;
  // The size gate rejects with 400 and a message that names what to change
  // ("~1,240 pages, limit 900 — expand fewer review states, or remove a
  // version"). Surfacing it beats a generic failure toast.
  throw new Error(response.data?.message || 'Request Failed');
}

async function bsaExportProgress(jobId) {
  const response = await apiClient.get(`/bsa/export/progress`, { jobId });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function bsaListExports(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.bsaProjectId) queryParams.append('bsaProjectId', params.bsaProjectId);
  if (params.vendorId) queryParams.append('vendorId', params.vendorId);
  if (params.jobId) queryParams.append('jobId', params.jobId);

  const queryString = queryParams.toString();
  const url = queryString ? `/bsa/export/list?${queryString}` : `/bsa/export/list`;

  const response = await apiClient.get(url);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function listManagedProducts(vendorId) {
  // const response = await apiClient.get(`/managedProducts?vendorId=`);
  const response = await apiClient.get(`/managedProducts?vendorId=${vendorId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function listUsers() {
  const response = await apiClient.get(`/user`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function listRbacGroups() {
  const response = await apiClient.get(`/auth/groups`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getManagedProduct(productId) {
  const response = await apiClient.get(`/managedProducts?id=${encodeURIComponent(productId)}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getDashboard() {
  const response = await apiClient.get(`/dashboard`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getVendorProducts(vendorId) {
  const response = await apiClient.get(`/vendors/${vendorId}/products`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getVendorExecutionStatus(vendorId) {
  const response = await apiClient.get(`/vendors/executionStatus/${encodeURIComponent(vendorId)}`);
  if (response.status === 200) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getVendorsRunningStatus() {
  const response = await apiClient.get('/vendors/executionStatus');
  if (response.status === 200) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getComponents(vendorId, query, offset, limit) {
  const response = await apiClient.get(
    `/vendors/bugComponents/${vendorId}?searchField=vendorBugComponents&query=${query}&offset=${offset}&limit=${limit}`
  );
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getVendor(vendorId) {
  const response = await apiClient.get(`/vendors?vendorId=${encodeURIComponent(vendorId)}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function vendorTestCreds(vendorId, data) {
  const response = await apiClient.post(`/vendors/testCredentials/${vendorId}`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function vmWareWarmup(data) {
  const response = await apiClient.post(`/vendors/lambdaWarmup`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function integrationServiceNowTest(data) {
  const response = await apiClient.post(`/integration/servicenow?action=testCreds`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function integrationSplunkTest(data) {
  const response = await apiClient.post(`/integration/splunk?action=testCreds`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function sNConectionTest() {
  const response = await apiClient.get(`/integration/servicenow?action=testConnection`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function openAiTest() {
  try {
    const response = await apiClient.get('/dashboard/vendorHealthStatus?vendorIds=openai');
    if (response.status === 200 || response.status === 201) {
      const data = response.data;
      const openAiVendor = data?.vendors?.find(v => v.vendorId === 'openai');
      if (openAiVendor?.currentStatus?.vendorData) {
        return openAiVendor.currentStatus.vendorData;
      }
      // No OpenAI data found, return operational
      return { service_issues: false, status: 'OPERATIONAL' };
    }
    throw new Error('Request Failed');
  } catch (error) {
    console.warn('OpenAI health check failed, returning default operational status', error);
    return { service_issues: false, status: 'OPERATIONAL' };
  }
}
async function integrationServiceNowCompanies(data) {
  const response = await apiClient.get(`/integration/servicenow?action=companies`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function integrationServiceNowCompaniesSearch(company) {
  const response = await apiClient.get(
    `/integration/servicenow?action=companiesSearch&company=${encodeURIComponent(company)}`
  );
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getServiceNowStats(options = {}) {
  const params = new URLSearchParams();
  params.append('action', 'stats');
  if (options.days) params.append('days', options.days);

  const response = await apiClient.get(`/integration/servicenow?${params.toString()}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getVendors() {
  const response = await apiClient.get(`/vendors`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getRbacRoles() {
  const response = await apiClient.get(`/auth/role`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function updateRbacRole(roleId, data) {
  const response = await apiClient.put(`/auth/role/${roleId}`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function updateRbacGroup(data) {
  const response = await apiClient.put(`/auth/group`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function updateUser(userId, data) {
  const response = await apiClient.put(`/user?userId=${userId}`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function createRbacRole(data) {
  const response = await apiClient.post(`/auth/role`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function getRbacRole(roleId) {
  const response = await apiClient.get(`/auth/role/${roleId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getCiscoProductIdBySerialNumber(serialNumber) {
  const response = await apiClient.get(`/vendors/cisco/productIdBySn/${serialNumber}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function createOrUpdateManagedProduct(body) {
  const response = await apiClient.post('/managedProducts', body);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function bulkUpdateManagedProducts(body) {
  const response = await apiClient.post('/managedProducts', body);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function deleteManagedProduct(productId) {
  const response = await apiClient.delete(`/managedProducts?id=${encodeURIComponent(productId)}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getSetting(settingId) {
  const response = await apiClient.get(`/setting?settingId=${encodeURIComponent(settingId)}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function toggleVendor(vendorId, vendorActive) {
  const active = vendorActive === false ? '0' : '1';
  const response = await apiClient.put(`/vendors?vendorId=${encodeURIComponent(vendorId)}&active=${active}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function updateVendorBypassStatusIo(vendorId, bypassStatusIoCheck) {
  const response = await apiClient.patch(`/super-admin/vendors/${encodeURIComponent(vendorId)}/bypass-status-check`, {
    bypassStatusIoCheck
  });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getProductEnablement() {
  const response = await apiClient.get('/super-admin/products');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function updateProductEnablement(product, enabled) {
  const response = await apiClient.patch('/super-admin/products', { product, enabled });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getPublicIP() {
  const response = await apiClient.get('/setting/publicIp');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getSSOStatus() {
  const response = await apiClient.get('/integration/sso');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getIntegrationSplunkStatus() {
  const response = await apiClient.get('/integration/splunk/status');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getAccountInfo() {
  const response = await apiClient.get('/setting/accountInfo');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getAppVersion() {
  const response = await apiClient.get('/setting/appVersion');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function updateSSOSettings(body) {
  const response = await apiClient.post('/integration/sso', body);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function updateIntegrationSplunkStatus(body) {
  const response = await apiClient.post('/integration/splunk/update', body);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getMyUser() {
  const response = await apiClient.get(`/user?action=myRbacUser`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function createOrUpdateAWSSecret(body) {
  const response = await apiClient.put('/setting?action=secret', body);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function saveSetting(settingId, syncOnly, body, isForceRun, isUpdatingCreds) {
  const params = new URLSearchParams();
  params.append('settingId', settingId);
  if (settingId !== 'servicenow') {
    params.append('syncOnly', syncOnly ? 'true' : 'false');
  }
  if (isForceRun) {
    params.append('forceRun', isForceRun);
  }
  if (isUpdatingCreds) {
    params.append('credentialsUpdated', isUpdatingCreds);
  }
  const response = await apiClient.post(`/setting?${params.toString()}`, body);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function createSupportTicket(body) {
  const response = await apiClient.post(`/support/ticket`, body);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function viewSupportTickets(bz_tenant_id) {
  const response = await apiClient.post(`/support/listTickets`, bz_tenant_id);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function createKey(name) {
  const response = await apiClient.post(`/auth/key`, name);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function deleteKey(keyId) {
  const response = await apiClient.delete(`/auth/key/${keyId}`);
  if (response.status === 200 || response.status === 201 || response.status === 202) return response;
  throw new Error('Request Failed');
}
async function deleteRbacRole(roleId) {
  const response = await apiClient.delete(`/auth/role/${roleId}`);
  if (response.status === 200 || response.status === 201 || response.status === 202) return response;
  throw new Error('Request Failed');
}
async function deleteRbacGroup(groupId) {
  const response = await apiClient.delete(`/auth/groups/${groupId}`);
  if (response.status === 200 || response.status === 201 || response.status === 202) return response;
  throw new Error('Request Failed');
}
async function getUserRbacRoles(userId) {
  const response = await apiClient.get(`/auth/userRoles/${userId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getGroupRbacRoles(groupId) {
  const response = await apiClient.get(`/auth/groupRoles/${groupId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getMyUserPermissions() {
  const response = await apiClient.get(`/auth/myUserPermissions`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getMyUserRoles() {
  const response = await apiClient.get(`/auth/myUserRoles`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getMyUserGroups() {
  const response = await apiClient.get(`/auth/myUserGroups`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getKeys() {
  const response = await apiClient.get(`/auth/keys`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function enableKey(apiKeyId, enable) {
  const response = await apiClient.get(`/auth/enableKey/${apiKeyId}/${enable}`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function enableApiKeyCreation(enable) {
  const response = await apiClient.get(`/auth/enableApi/${enable}`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function getStatus(vendorId) {
  const response = await apiClient.get(`/service?vendorId=${encodeURIComponent(vendorId)}`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function setStatus(vendorId, status) {
  const response = await apiClient.put(`/service?vendorId=${encodeURIComponent(vendorId)}&enable=${status}`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function getBzDashboard() {
  const response = await apiClient.get(`/setting?settingId=bugzero`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function postBzDashboard(settings) {
  const response = await apiClient.post(`/setting?settingId=bugzero`, settings);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function getVendorHealthStatus(vendorId = null) {
  try {
    const url = vendorId ? `/dashboard/vendorHealthStatus?vendorId=${vendorId}` : `/dashboard/vendorHealthStatus`;
    const response = await apiClient.get(url);
    if (response.status === 200 || response.status === 201) return response.data;
    throw new Error('API request failed');
  } catch (error) {
    console.warn('Using mock data for vendor health status', error);
    // Use mock data during development
    const mockData = await import('../__mocks__/vendorHealthStatus.json');
    const data = mockData.default || mockData;
    // If vendorId is provided, filter to return only that vendor's data
    if (vendorId && data.vendors) {
      const filteredVendors = data.vendors.filter(v => v.vendorId === vendorId || v.vendorId.startsWith(vendorId));
      return { ...data, vendors: filteredVendors };
    }
    return data;
  }
}
async function getBugServiceSchedule(vendorId) {
  const response = await apiClient.get(`/vendors/bugServiceSchedule/${vendorId}`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function postBugServiceSchedule(vendorId, data) {
  const response = await apiClient.post(`/vendors/bugServiceSchedule/${vendorId}`, data);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function getBugServiceHistory(vendorId, type) {
  const response = await apiClient.get(`/dashboard/history?vendorId=${vendorId}&type=${type}&count=10`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}
async function getExecutionHistory(vendorId, options = {}) {
  const params = new URLSearchParams();
  params.append('vendor_id', vendorId);
  if (options.count) params.append('count', options.count);
  if (options.aggregatedDaysRange !== undefined) params.append('aggregated_days_range', options.aggregatedDaysRange);
  if (options.lastDays) params.append('last_days', options.lastDays);

  const response = await apiClient.get(`/dashboard/executionHistory?${params.toString()}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function postAgreement(settingId, data) {
  const response = await apiClient.post(`/setting?action=disclaimer&settingId=${encodeURIComponent(settingId)}`, data);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}

// Vendor API health checks - Deprecated: Return hardcoded operational status
async function getAzureHealthCheck(vendorId) {
  // Deprecated: Returns hardcoded operational status until consolidated health endpoint ships
  return { status: 200, data: { service_issues: false, status: 'OPERATIONAL' } };
}
async function getMsftHealthCheck(vendorId) {
  // Deprecated: Returns hardcoded operational status until consolidated health endpoint ships
  return { status: 200, data: { service_issues: false, status: 'OPERATIONAL' } };
}
async function getAwsHealthCheck(vendorId) {
  // Deprecated: Returns hardcoded operational status until consolidated health endpoint ships
  return { status: 200, data: { service_issues: false, status: 'OPERATIONAL' } };
}
async function getCiscoHealthCheck(vendorId) {
  // Deprecated: Returns hardcoded operational status until consolidated health endpoint ships
  return { status: 200, data: { service_issues: false, status: 'OPERATIONAL' } };
}
async function getVendorHealthCheck(vendorId) {
  // Deprecated: Returns hardcoded operational status until consolidated health endpoint ships
  return { status: 200, data: { service_issues: false, status: 'OPERATIONAL' } };
}
async function getAwsOrgHealthSupport() {
  const response = await apiClient.get(`/vendors/awsOrgHealthSupport`);
  if (response.status === 200 || response.status === 201) return response;
  throw new Error('Request Failed');
}

// WAF API functions
async function getWafStatus() {
  const response = await apiClient.get('/waf/whitelist/status');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
/**
 * Enables WAF with the specified configuration
 * @param {Object} data - The WAF configuration object
 * @param {string[]} data.allowedIps - List of IP addresses to whitelist
 * @returns {Promise<Object>} The WAF status after enabling
 */

async function enableWaf(data = {}) {
  const response = await apiClient.post('/waf/whitelist/enable', data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function disableWaf() {
  const response = await apiClient.post('/waf/whitelist/disable');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function getWafIpAddresses() {
  const response = await apiClient.get('/waf/ipset/addresses');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function addWafIpAddress(data) {
  const response = await apiClient.post('/waf/ipset/addresses', data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;

  const errorMessage = response.data?.error || 'WAF IP Address addition failed';
  throw new Error(`WAF Error: ${errorMessage} (${response.status})`);
}

async function deleteWafIpAddress(ipAddress) {
  const response = await apiClient.delete('/waf/ipset/addresses/', {}, { data: { ipAddress } });
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

// Tenant Settings API methods
async function getTenantSettings() {
  const response = await apiClient.get('/tenantSettings');
  if (response.status === 200) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function getTenantSetting(settingId) {
  const response = await apiClient.get(`/tenantSettings?settingId=${encodeURIComponent(settingId)}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}
async function updateTenantSetting(settingId, data) {
  const response = await apiClient.put(`/tenantSettings?settingId=${encodeURIComponent(settingId)}`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

// Vendor AI Settings API methods
async function getVendorAiSettings() {
  const response = await apiClient.get('/vendorAiSettings');
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function getVendorAiSetting(vendorId) {
  const response = await apiClient.get(`/vendorAiSettings?vendorId=${vendorId}`);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

async function updateVendorAiSetting(vendorId, data) {
  const response = await apiClient.put(`/vendorAiSettings/${vendorId}`, data);
  if (response.status === 200 || response.status === 201) return response.data ? response.data : null;
  throw new Error('Request Failed');
}

export default {
  apiClient,
  getAppVersion,
  createSupportTicket,
  viewSupportTickets,
  listManagedProducts,
  listUsers,
  listRbacGroups,
  getManagedProduct,
  getDashboard,
  getSSOStatus,
  getVendorProducts,
  getVendorExecutionStatus,
  getVendorsRunningStatus,
  getVendor,
  getVendors,
  createOrUpdateManagedProduct,
  bulkUpdateManagedProducts,
  deleteManagedProduct,
  getSetting,
  openAiTest,
  saveSetting,
  vendorTestCreds,
  updateSSOSettings,
  updateIntegrationSplunkStatus,
  getCiscoProductIdBySerialNumber,
  createOrUpdateAWSSecret,
  integrationServiceNowTest,
  integrationSplunkTest,
  sNConectionTest,
  integrationServiceNowCompanies,
  integrationServiceNowCompaniesSearch,
  getServiceNowStats,
  getComponents,
  createKey,
  deleteKey,
  getKeys,
  enableKey,
  enableApiKeyCreation,
  getRbacRole,
  createRbacRole,
  updateRbacRole,
  updateRbacGroup,
  deleteRbacRole,
  deleteRbacGroup,
  getRbacRoles,
  getMyUserPermissions,
  getMyUserRoles,
  getMyUserGroups,
  updateUser,
  getPublicIP,
  getAccountInfo,
  toggleVendor,
  updateVendorBypassStatusIo,
  getProductEnablement,
  updateProductEnablement,
  getStatus,
  getIntegrationSplunkStatus,
  setStatus,
  getBzDashboard,
  postBzDashboard,
  getVendorHealthStatus,
  getBugServiceSchedule,
  getUserRbacRoles,
  getGroupRbacRoles,
  getMyUser,
  postBugServiceSchedule,
  vmWareWarmup,
  getBugServiceHistory,
  getExecutionHistory,
  postAgreement,
  getAzureHealthCheck,
  getMsftHealthCheck,
  getAwsHealthCheck,
  getCiscoHealthCheck,
  getVendorHealthCheck,
  getAwsOrgHealthSupport,
  bsaCreateProject,
  bsaDeleteProject,
  bsaSetVersionBugCveState,
  bsaCreateVersionComment,
  bsaDeleteVersionComment,
  bsaCreateBugOrCveComment,
  bsaDeleteBugOrCveComment,
  bsaSetVersionState,
  bsaListProjects,
  bsaGetProject,
  bsaListProjectVersions,
  bsaInvVendors,
  bsaInvCounts,
  bsaInvStartFetch,
  bsaInvGetFetchJob,
  bsaInvCreateProjectFromInventory,
  bsaInvDiscover,
  bsaInvStartDiscoverScan,
  bsaInvRefreshSnapshot,
  bsaInvGetConfigRemoved,
  bsaInvSetComponentOverride,
  bsaInvGetManualComponents,
  bsaGetBstVendors,
  bsaGetBstVendorProducts,
  bsaGetBstSoftwareSuggestions,
  bsaGetBstCves,
  bsaGetBstBugs,
  bsaStartExport,
  bsaExportProgress,
  bsaListExports,
  getWafStatus,
  enableWaf,
  disableWaf,
  getWafIpAddresses,
  addWafIpAddress,
  deleteWafIpAddress,
  getTenantSettings,
  getTenantSetting,
  updateTenantSetting,
  getVendorAiSettings,
  getVendorAiSetting,
  updateVendorAiSetting
};
